// EduMisia analytics helper — GDPR-friendly.
//
// Loading strategy:
// 1. Nothing tracks a visitor until they actively click "Súhlasím" in the
//    cookie banner.
// 2. Consent choice is persisted in localStorage under `edumisia_cc_v1`.
// 3. On "accept" we (a) inject Google's gtag.js, (b) set Consent Mode v2 to
//    `analytics_storage: granted` while keeping every ads-flag `denied`, and
//    (c) send a `page_view` for the current URL.
// 4. `trackEvent(name, params)` is safe to call from anywhere — it silently
//    no-ops until GA4 is loaded, so component code can be dumb about state.
//
// This file MUST NOT import any React or add any DOM listeners at import
// time; App-level effects wire everything up.

const STORAGE_KEY = 'edumisia_cc_v1'
const GA4_MEASUREMENT_ID = 'G-9XQX838F8T'

let ga4Loaded = false

/** Read the current consent choice. `null` = user has not decided yet. */
export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && (parsed.analytics === true || parsed.analytics === false)) {
      return parsed
    }
    return null
  } catch {
    return null
  }
}

/** Persist consent choice. Does NOT itself send any tracking. */
export function setConsent({ analytics }) {
  const record = {
    analytics: analytics === true,
    updated_at: new Date().toISOString(),
    version: 1,
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record))
  } catch {
    /* localStorage disabled — treat as ephemeral. */
  }
  return record
}

/** Ensure GA4 script is present and configured for the given consent. */
export function initAnalytics(consent) {
  if (typeof window === 'undefined') return
  if (!consent || consent.analytics !== true) return
  if (ga4Loaded) {
    // Already active — nothing to do.
    updateConsent(consent)
    return
  }
  // 1) Bootstrap dataLayer + gtag stub BEFORE loading the script so we can
  //    queue the initial `consent default` and later calls safely.
  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() { window.dataLayer.push(arguments) }
  // 2) Default all storage denied. Any pre-load queued events therefore run
  //    in "no cookies" mode; we then update analytics_storage to granted.
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  })
  // 3) Inject the gtag.js script.
  const s = document.createElement('script')
  s.async = true
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`
  document.head.appendChild(s)
  // 4) Configure GA4. `send_page_view: false` — we send it explicitly from
  //    the router so SPA route changes are captured consistently.
  window.gtag('js', new Date())
  window.gtag('config', GA4_MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  })
  ga4Loaded = true
  // 5) Flip analytics_storage to granted now that the user consented.
  window.gtag('consent', 'update', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  })
  // 6) Send the very first page_view for the current URL.
  trackEvent('page_view', {
    page_path: window.location.pathname + window.location.search,
    page_location: window.location.href,
    page_title: document.title,
  })
}

/** Push consent updates to GA4 mid-session (used when user changes choice). */
export function updateConsent(consent) {
  if (typeof window === 'undefined') return
  if (!window.gtag) return
  window.gtag('consent', 'update', {
    analytics_storage: consent?.analytics === true ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  })
}

/** Silent no-op unless analytics is loaded (i.e., consent granted). */
export function trackEvent(name, params = {}) {
  if (typeof window === 'undefined') return
  if (!window.gtag || !ga4Loaded) return
  // Defensive: strip anything that even looks like PII. Values that survive
  // this filter are pure enumerations / counts / test IDs.
  const clean = {}
  for (const [k, v] of Object.entries(params || {})) {
    if (v === null || v === undefined) continue
    const key = String(k).toLowerCase()
    if (['email', 'name', 'phone', 'user_id', 'uid', 'user',
      'session_id', 'stripe_session_id', 'payment_id',
      'stripe_payment_id', 'message', 'text', 'display_name'
    ].includes(key)) continue
    // Never send free-text longer than 60 chars (defense in depth).
    if (typeof v === 'string' && v.length > 60) continue
    clean[k] = v
  }
  try {
    window.gtag('event', name, clean)
  } catch {
    /* GA4 initialisation failed silently — do not disrupt the app. */
  }
}

/** Fire a fresh page_view for SPA route changes (after initial load). */
export function trackPageView(pathname, title) {
  trackEvent('page_view', {
    page_path: pathname,
    page_title: title,
    page_location: typeof window !== 'undefined' ? window.location.href : undefined,
  })
}

// A tiny cross-component "open cookie settings" bus. The CookieBanner
// subscribes to this so a footer link (or any component) can re-open the
// consent choice without direct wiring.
const OPEN_SETTINGS_EVENT = 'edumisia:open-cookie-settings'
export function openConsentSettings() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT))
}
export function onOpenConsentSettings(handler) {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(OPEN_SETTINGS_EVENT, handler)
  return () => window.removeEventListener(OPEN_SETTINGS_EVENT, handler)
}

export const ANALYTICS_STORAGE_KEY = STORAGE_KEY
